/**
 * product-card.js — Product card interactivity
 *
 * Responsibilities:
 *   1. Wishlist toggle — toggles .is-active + aria-pressed on .product-card__wishlist.
 *      Backend integration is Phase 5+; this is client-state only.
 *
 *   2. Quick-add form — intercepts native form submit, posts to /cart/add via fetch,
 *      dispatches cart:updated event so the header badge updates (see header.js listener).
 *
 *   3. Swatch hover preview — on .product-card__swatch mouseenter, swaps the
 *      primary image src to the variant image if the variant has one.
 *      Falls back gracefully if no variant image is found.
 *
 * No framework. Vanilla ES module. Delegated event listeners on document.
 */

(() => {
  'use strict';

  /* Idempotency guard — multiple sections (fresh-drops, bestsellers, related,
     etc.) emit a <script src="product-card.js"> tag, so this IIFE can run
     N times in a single page. Without this guard each document-level click
     listener was being registered N times, firing N times per real click.
     For odd N and even image counts the visual swap reverted to start
     ("0 → 1 → 0 → 1" cycles back when N=3 and images=3). */
  if (window.__kissaProductCardInit) return;
  window.__kissaProductCardInit = true;

  /* ============================================================
     1. Wishlist toggle
     ============================================================ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.product-card__wishlist');
    if (!btn) return;
    e.preventDefault();

    const isActive = btn.getAttribute('aria-pressed') === 'true';
    btn.setAttribute('aria-pressed', isActive ? 'false' : 'true');
    btn.classList.toggle('is-active', !isActive);
  });

  /* ============================================================
     2. Size chip click — selection, NOT cart add.
        Old behavior: chip was a form-submit that POSTed to /cart/add.
        New behavior: chip click toggles is-active among siblings,
        updates the Size: X label, and refreshes card.dataset.activeVariantId
        based on the new (size, pattern) combination. Cart-add lives in
        the separate `+ Add` pill (handler 5 below).
     ============================================================ */
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('.product-card__size-chip');
    if (!chip || chip.disabled) return;

    const card = chip.closest('.product-card');
    if (!card) return;

    const sizeValue = chip.dataset.sizeValue;
    if (!sizeValue) return;

    e.preventDefault();
    e.stopPropagation();

    /* Toggle is-active among sibling chips */
    card.querySelectorAll('.product-card__size-chip.is-active').forEach((c) => {
      c.classList.remove('is-active');
      c.setAttribute('aria-pressed', 'false');
    });
    chip.classList.add('is-active');
    chip.setAttribute('aria-pressed', 'true');

    /* Update card state + the Size: X label */
    card.dataset.activeSize = sizeValue;
    const sizeLabel = card.querySelector('[data-option-name="size"]');
    if (sizeLabel) sizeLabel.textContent = sizeValue;

    /* Find variant matching (active pattern, new size). If none available,
       clear activeVariantId so the + Add button can refuse the post (BUG-2
       from review: stale variant id was being kept and posted). */
    const matched = findMatchingVariant(card);
    if (matched) {
      card.dataset.activeVariantId = matched.id;
    } else {
      delete card.dataset.activeVariantId;
    }
    refreshAddButtonState(card);
  });

  /* ============================================================
     5. + Add button click — post current active variant to cart.
        Reads card.dataset.activeVariantId (kept current by handlers 2 + 3a)
        so this button always reflects the selected (size, pattern) combo.
     ============================================================ */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-card-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    e.stopPropagation();

    const card = btn.closest('.product-card');
    if (!card) return;

    /* Resolve fresh. dataset.activeVariantId is canonical — kept in sync by
       handlers 2 + 3a on every size/pattern change. If it's absent the
       card is in an unavailable (size, pattern) combo: refuse the post
       (review BUG-2: was posting the previous variant's id silently). */
    let variantId = card.dataset.activeVariantId;
    if (!variantId) {
      /* Try once more with a fresh lookup — may have been cleared by a
         click that fired without a working pattern in dataset. */
      const matched = findMatchingVariant(card);
      if (matched) {
        variantId = matched.id;
        card.dataset.activeVariantId = variantId;
      }
    }
    if (!variantId) {
      /* Visible feedback that something's off — flash the button border
         briefly. Better than a silent no-op. */
      btn.classList.add('product-card__add-btn--invalid');
      setTimeout(() => btn.classList.remove('product-card__add-btn--invalid'), 600);
      return;
    }

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-loading');
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        console.warn('[product-card] cart/add error:', err.description || response.status);
        /* Visible failure cue — same shake we use for the no-variant case. */
        btn.classList.add('product-card__add-btn--invalid');
        setTimeout(() => btn.classList.remove('product-card__add-btn--invalid'), 600);
        return;
      }
      const cart = await fetch(window.Shopify.routes.root + 'cart.js').then((r) => r.json());
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true, detail: { cart } }));
    } catch (err) {
      console.warn('[product-card] add-to-cart error:', err);
      btn.classList.add('product-card__add-btn--invalid');
      setTimeout(() => btn.classList.remove('product-card__add-btn--invalid'), 600);
    } finally {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.classList.remove('is-loading');
    }
  });

  /**
   * Find the variant matching the card's current activeSize + activePattern
   * via the JSON `variants_data` payload. Returns the variant object or null.
   */
  function findMatchingVariant(card) {
    const data = readImageData(card);
    if (!data.variants_data || !data.option_position) return null;
    const sizePos = data.option_position.size;
    const patternPos = data.option_position.pattern;
    const activeSize = card.dataset.activeSize;
    const activePattern = card.dataset.activePattern;
    return data.variants_data.find((v) => {
      const sizeMatch = sizePos < 0 || v.options[sizePos] === activeSize;
      const patternMatch = patternPos < 0 || v.options[patternPos] === activePattern;
      return sizeMatch && patternMatch && v.available;
    }) || null;
  }

  /* ============================================================
     3a. Swatch click — switch active variant, update carousel state.
         Each swatch is an <a href="?variant=id"> so modifier-clicks
         still work as PDP shortcuts (open in new tab, etc.). A plain
         left-click swaps the card to that variant's image set IN PLACE
         and toggles chevron visibility based on the new image count.
     ============================================================ */
  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('.product-card__swatch');
    if (!swatch) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

    const card = swatch.closest('.product-card');
    if (!card) return;

    e.preventDefault();

    /* Update visual selection on the swatch row. */
    card.querySelectorAll('.product-card__swatch.is-active').forEach((s) =>
      s.classList.remove('is-active')
    );
    swatch.classList.add('is-active');

    const variantId = swatch.dataset.variantId;
    const media = card.querySelector('.product-card__media');
    const primaryImg = card.querySelector('.product-card__image--primary');
    if (!media || !primaryImg) return;

    /* Look up the variant's image index from the JSON payload. The
       carousel still cycles through ALL product media — swatch click
       only jumps the index, doesn't filter the set. */
    const data = readImageData(card);
    const images = data.images || [];
    let targetIndex = 0;
    if (variantId && data.variant_image_index && variantId in data.variant_image_index) {
      targetIndex = data.variant_image_index[variantId] | 0;
    }

    if (variantId) card.dataset.activeVariantId = variantId;
    media.dataset.imageIndex = String(targetIndex);

    /* Track the active pattern at the card level so size-chip clicks can
       compute the matching (size, pattern) variant later. */
    if (swatch.dataset.swatchValue) {
      card.dataset.activePattern = swatch.dataset.swatchValue;
    }

    /* aria-pressed toggle — swatch row was screen-reader-invisible without it
       (review issue 4). Active swatch is the only one with aria-pressed=true. */
    card.querySelectorAll('.product-card__swatch.is-active').forEach((s) => {
      if (s !== swatch) {
        s.classList.remove('is-active');
        s.setAttribute('aria-pressed', 'false');
      }
    });
    swatch.classList.add('is-active');
    swatch.setAttribute('aria-pressed', 'true');

    /* Update the pattern label in the option-summary block to match the
       clicked swatch. Size label is left alone — that's selected via
       the size-chip flow (separately). */
    const patternLabelEl = card.querySelector('[data-option-name="pattern"]');
    if (patternLabelEl && swatch.dataset.swatchValue) {
      patternLabelEl.textContent = swatch.dataset.swatchValue;
    }

    /* Refresh size chip availability for the new pattern (review BUG-3:
       a chip disabled state was based on "any variant in this size is
       available" — but with multiple options, "M Red" can be sold out
       while "M Blue" is in stock; the chip needs per-pattern refresh). */
    refreshChipAvailability(card);
    refreshAddButtonState(card);

    const next = images[targetIndex];
    if (next && next.src) {
      /* Synchronous src swap — browser will fade in the new image as it
         decodes. Avoids the async preload+decode pattern that was hanging
         the swatch handler indefinitely (finally block never reached → src
         never swapped). The browser caches the image after first decode
         so subsequent clicks on the same swatch are instant. */
      primaryImg.src = next.src;
      primaryImg.srcset = next.srcset || '';
      if (next.alt) primaryImg.alt = next.alt;
    }
  });

  /**
   * Image-data cache. Keyed by product id (NOT the DOM node) so that future
   * features like Quick View — which may swap card content in-place — get
   * fresh data after the swap (review issue 5: DOM-node-keyed cache held
   * stale data when the same node was reused for a different product).
   */
  const imageDataCache = new Map();

  function readImageData(card) {
    const productId = card.dataset.productId || card.dataset.productHandle || '';
    if (productId && imageDataCache.has(productId)) return imageDataCache.get(productId);
    const dataScript = card.querySelector('.product-card__image-data');
    const fallback = { default_variant_id: '', images: [], variants_data: [], variant_image_index: {}, option_position: { size: -1, pattern: -1 } };
    if (!dataScript) return fallback;
    try {
      const parsed = JSON.parse(dataScript.textContent);
      if (productId) imageDataCache.set(productId, parsed);
      return parsed;
    } catch (err) {
      console.warn('[product-card] failed to parse image-data JSON', err);
      return fallback;
    }
  }

  /**
   * Updates each size chip's disabled state based on availability for the
   * card's current activePattern. Called after swatch click — the static
   * Liquid render only knew about the initial pattern, so chip availability
   * must be re-evaluated client-side when the user picks a different one.
   */
  function refreshChipAvailability(card) {
    const data = readImageData(card);
    if (!data.variants_data || !data.option_position) return;
    const { size: sizePos, pattern: patternPos } = data.option_position;
    if (sizePos < 0) return;
    const activePattern = card.dataset.activePattern;

    card.querySelectorAll('.product-card__size-chip').forEach((chip) => {
      const sizeValue = chip.dataset.sizeValue;
      if (!sizeValue) return;
      const hasMatch = data.variants_data.some((v) => {
        const sizeMatch = v.options[sizePos] === sizeValue;
        const patternMatch = patternPos < 0 || v.options[patternPos] === activePattern;
        return sizeMatch && patternMatch && v.available;
      });
      chip.disabled = !hasMatch;
      chip.classList.toggle('product-card__size-chip--unavailable', !hasMatch);
    });
  }

  /**
   * Disables the + Add button when there's no usable activeVariantId. Called
   * after any state change that could leave the card without a valid variant
   * (size click for an unavailable combo, swatch change with new disabled
   * sizes, etc.).
   */
  function refreshAddButtonState(card) {
    const btn = card.querySelector('[data-card-add]');
    if (!btn) return;
    btn.disabled = !card.dataset.activeVariantId;
  }

  /**
   * Resolves once the image at the given URL has loaded (or errored).
   * Uses the Image() constructor + decode() when supported for a cleaner
   * "ready to paint" signal than onload alone.
   */
  function preloadImage(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (img.decode) {
          img.decode().then(resolve).catch(resolve);
        } else {
          resolve();
        }
      };
      img.onerror = resolve;
      img.src = url;
    });
  }

  /* 3b removed: swatch HOVER no longer swaps the primary image to the
     variant image. The chevron-cycle now owns the image-swap behavior;
     having two systems compete for the same <img> src caused user-visible
     glitches. Swatch CLICK (handler 3a above) still swaps the variant
     image — that's the explicit user action, kept as-is. */

  /**
   * Fetches the product JSON and finds the first variant image for a given color.
   * Caches per product handle to avoid redundant network requests.
   *
   * @param {string} productHandle
   * @param {string} colorValue
   * @returns {Promise<string|null>} image URL or null
   */
  const productCache = new Map();

  async function fetchVariantImageForColor(productHandle, colorValue) {
    if (!productCache.has(productHandle)) {
      try {
        const url = `${window.Shopify.routes.root}products/${productHandle}.js`;
        const resp = await fetch(url);
        if (!resp.ok) return null;
        const data = await resp.json();
        productCache.set(productHandle, data || null);
      } catch {
        return null;
      }
    }

    const product = productCache.get(productHandle);
    if (!product) return null;

    const colorLower = colorValue.toLowerCase();
    const matchedVariant = (product.variants || []).find((v) =>
      (v.option1 || '').toLowerCase() === colorLower ||
      (v.option2 || '').toLowerCase() === colorLower ||
      (v.option3 || '').toLowerCase() === colorLower
    );

    if (!matchedVariant || !matchedVariant.featured_image) return null;

    const img = matchedVariant.featured_image;
    return img.src ? img.src.replace(/\.(jpg|jpeg|png|gif|webp)/, '_400x.$1') : null;
  }

  /* Section 4 (chevron image-cycle handler) was removed: the CSS-only
     hover-swap between primary/secondary images now owns image preview
     behavior on the card. Swatch click (handler 3a) still owns the
     explicit variant image swap. */
})();
